import React, { useState, useEffect } from 'react';
import './Discover.css';
import { useAuth } from '../../contexts/authContext';
import supabase from "../../supabaseClient";
import authFetch from '../../utils/authFetch';

import DiscoverTabs from './Components/DiscoverTabs';
import DiscoverFilters from './Components/DiscoverFilters';
import UserGrid from './Components/UserGrid';
import IncomingRequests from './Components/IncomingRequests';
import SuggestionsModal from './Components/SuggestionsModal';
import GroupGrid from './Components/GroupGrid';
import EventSection from './Components/EventSection';
import NotificationsPanel from '../../panels/NotificationsPanel';

const Discover = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [connectionRequests, setConnectionRequests] = useState({});
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [groupMemberships, setGroupMemberships] = useState({});
  const [eventAttendance, setEventAttendance] = useState({});
  const [allEvents, setAllEvents] = useState([]);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    language: '',
    created_by: user?.id || '',
    created_at: new Date().toISOString(),
  });
  
  const [languageFilter, setLanguageFilter] = useState('');
  const [nativeLanguageFilter, setNativeLanguageFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [search, setSearch] = useState('');
  const [usersSort, setUsersSort] = useState('best_match');
  const [includeConnectedUsers, setIncludeConnectedUsers] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [nativeLanguageOptions, setNativeLanguageOptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [userFacets, setUserFacets] = useState({ targetLanguages: [], nativeLanguages: [], levels: [] });
  const [usersPage, setUsersPage] = useState(1);
  const [usersNextPage, setUsersNextPage] = useState(null);
  const [connectedUserIds, setConnectedUserIds] = useState([]); // accepted connections both directions
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [groupFacets, setGroupFacets] = useState({ languages: [], levels: [], days: [] });
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsNextPage, setGroupsNextPage] = useState(null);
  const [eventAttendees, setEventAttendees] = useState({});
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [eventFacets, setEventFacets] = useState({ languages: [], dateMin: null, dateMax: null });
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsNextPage, setEventsNextPage] = useState(null);
  const [myMatchPreferences, setMyMatchPreferences] = useState(null);

  const [groupSearch, setGroupSearch] = useState('');
  const [groupLanguage, setGroupLanguage] = useState('');
  const [groupLevel, setGroupLevel] = useState('');
  const [groupDayOfWeek, setGroupDayOfWeek] = useState('');
  const [groupTimeFrom, setGroupTimeFrom] = useState('');
  const [groupTimeTo, setGroupTimeTo] = useState('');
  const [groupsSort, setGroupsSort] = useState('best_match');

  const [eventSearch, setEventSearch] = useState('');
  const [eventLanguage, setEventLanguage] = useState('');
  const [eventDateFrom, setEventDateFrom] = useState('');
  const [eventDateTo, setEventDateTo] = useState('');
  const [eventUpcomingOnly, setEventUpcomingOnly] = useState(true);
  const [eventAttendingOnly, setEventAttendingOnly] = useState(false);
  const [eventsSort, setEventsSort] = useState('soonest');

  const discoverFiltersStorageKey = user?.id ? `discover_filters_v1:${user.id}` : 'discover_filters_v1:anon';

  useEffect(() => {
    // Restore last-used filters (per tab) on mount/user change
    try {
      const raw = localStorage.getItem(discoverFiltersStorageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return;

      const u = parsed.users || {};
      if (typeof u === 'object') {
        setLanguageFilter(u.languageFilter || '');
        setNativeLanguageFilter(u.nativeLanguageFilter || '');
        setLevelFilter(u.levelFilter || '');
        setSearch(u.search || '');
        setUsersSort(u.usersSort || 'best_match');
        setIncludeConnectedUsers(typeof u.includeConnectedUsers === 'boolean' ? u.includeConnectedUsers : false);
      }

      const g = parsed.groups || {};
      if (typeof g === 'object') {
        setGroupSearch(g.groupSearch || '');
        setGroupLanguage(g.groupLanguage || '');
        setGroupLevel(g.groupLevel || '');
        setGroupDayOfWeek(g.groupDayOfWeek || '');
        setGroupTimeFrom(g.groupTimeFrom || '');
        setGroupTimeTo(g.groupTimeTo || '');
        setGroupsSort(g.groupsSort || 'best_match');
      }

      const e = parsed.events || {};
      if (typeof e === 'object') {
        setEventSearch(e.eventSearch || '');
        setEventLanguage(e.eventLanguage || '');
        setEventDateFrom(e.eventDateFrom || '');
        setEventDateTo(e.eventDateTo || '');
        setEventUpcomingOnly(typeof e.eventUpcomingOnly === 'boolean' ? e.eventUpcomingOnly : true);
        setEventAttendingOnly(typeof e.eventAttendingOnly === 'boolean' ? e.eventAttendingOnly : false);
        setEventsSort(e.eventsSort || 'soonest');
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverFiltersStorageKey]);

  useEffect(() => {
    // Persist filters for all tabs
    try {
      const payload = {
        users: {
          languageFilter,
          nativeLanguageFilter,
          levelFilter,
          search,
          usersSort,
          includeConnectedUsers,
        },
        groups: {
          groupSearch,
          groupLanguage,
          groupLevel,
          groupDayOfWeek,
          groupTimeFrom,
          groupTimeTo,
          groupsSort,
        },
        events: {
          eventSearch,
          eventLanguage,
          eventDateFrom,
          eventDateTo,
          eventUpcomingOnly,
          eventAttendingOnly,
          eventsSort,
        },
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(discoverFiltersStorageKey, JSON.stringify(payload));
    } catch {}
  }, [
    discoverFiltersStorageKey,
    languageFilter,
    nativeLanguageFilter,
    levelFilter,
    search,
    usersSort,
    includeConnectedUsers,
    groupSearch,
    groupLanguage,
    groupLevel,
    groupDayOfWeek,
    groupTimeFrom,
    groupTimeTo,
    groupsSort,
    eventSearch,
    eventLanguage,
    eventDateFrom,
    eventDateTo,
    eventUpcomingOnly,
    eventAttendingOnly,
    eventsSort,
  ]);

  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('');
  const [debouncedEventSearch, setDebouncedEventSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(search || ''), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedGroupSearch(groupSearch || ''), 300);
    return () => clearTimeout(t);
  }, [groupSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedEventSearch(eventSearch || ''), 300);
    return () => clearTimeout(t);
  }, [eventSearch]);

  const [savedSearches, setSavedSearches] = useState([]);
  const [selectedSavedSearchId, setSelectedSavedSearchId] = useState('');

  const savedSearchStorageKey = user?.id ? `discover_saved_searches_users:${user.id}` : 'discover_saved_searches_users:anon';

  useEffect(() => {
    try {
      const raw = localStorage.getItem(savedSearchStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setSavedSearches(Array.isArray(parsed) ? parsed : []);
    } catch {
      setSavedSearches([]);
    }
  }, [savedSearchStorageKey]);

  const persistSavedSearches = (next) => {
    setSavedSearches(next);
    try {
      localStorage.setItem(savedSearchStorageKey, JSON.stringify(next));
    } catch {}
  };

  const buildCurrentSavedSearch = () => ({
    id: `${Date.now()}`,
    name: '',
    filters: {
      languageFilter,
      nativeLanguageFilter,
      levelFilter,
      search,
    },
    createdAt: new Date().toISOString(),
  });

  const handleSaveCurrentSearch = () => {
    if (activeTab !== 'users') return;
    const name = window.prompt('Name this saved search:', '') || '';
    const trimmed = name.trim();
    if (!trimmed) return;
    const next = buildCurrentSavedSearch();
    next.name = trimmed;
    persistSavedSearches([next, ...(savedSearches || [])].slice(0, 30));
    setSelectedSavedSearchId(next.id);
  };

  const handleApplySavedSearch = () => {
    if (activeTab !== 'users') return;
    const item = (savedSearches || []).find((s) => String(s.id) === String(selectedSavedSearchId));
    if (!item?.filters) return;
    setLanguageFilter(item.filters.languageFilter || '');
    setNativeLanguageFilter(item.filters.nativeLanguageFilter || '');
    setLevelFilter(item.filters.levelFilter || '');
    setSearch(item.filters.search || '');
  };

  const handleDeleteSavedSearch = () => {
    if (activeTab !== 'users') return;
    if (!selectedSavedSearchId) return;
    const next = (savedSearches || []).filter((s) => String(s.id) !== String(selectedSavedSearchId));
    persistSavedSearches(next);
    setSelectedSavedSearchId('');
  };

  const buildDemographicsFromPreferences = (prefs) => {
    const mp = prefs && typeof prefs === 'object' ? prefs : {};
    const age = mp.preferred_age_range && typeof mp.preferred_age_range === 'object' ? mp.preferred_age_range : {};
    const genders = Array.isArray(mp.preferred_genders) ? mp.preferred_genders : [];

    const min = typeof age.min === 'number' ? age.min : null;
    const max = typeof age.max === 'number' ? age.max : null;
    const cleanedGenders = genders.map(String).filter(Boolean);

    if (min == null && max == null && cleanedGenders.length === 0) return null;
    return {
      ageRange: { min, max },
      genders: cleanedGenders,
    };
  };

  useEffect(() => {
    const loadMyMatchPreferences = async () => {
      if (!user?.id) {
        setMyMatchPreferences(null);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('match_preferences')
          .eq('id', user.id)
          .maybeSingle();
        if (error) throw error;
        const mp = (data && data.match_preferences && typeof data.match_preferences === 'object') ? data.match_preferences : null;
        setMyMatchPreferences(mp);
      } catch {
        setMyMatchPreferences(null);
      }
    };
    loadMyMatchPreferences();
  }, [user?.id]);

  const fetchUsersFromApi = async ({ page = 1, sort = usersSort || 'best_match', overrideFilters = null, append = false } = {}) => {
    if (!user?.id) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const demographicsFromPrefs = buildDemographicsFromPreferences(myMatchPreferences);
      const filters = overrideFilters || {
        targetLanguages: languageFilter ? [languageFilter] : [],
        nativeLanguages: nativeLanguageFilter ? [nativeLanguageFilter] : [],
        levels: levelFilter ? [levelFilter] : [],
        interests: [],
        search: debouncedUserSearch || '',
        demographics: demographicsFromPrefs || undefined,
        includeConnected: includeConnectedUsers,
      };

      const res = await authFetch('/api/discover/users/search', {
        method: 'POST',
        body: {
          filters,
          sort,
          page,
          pageSize: 24,
        },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Failed to load users');
      }
      const payload = await res.json();
      const results = payload?.results || [];
      const facets = payload?.facets || null;
      setUsers((prev) => (append ? [...(prev || []), ...(results || [])] : results));
      setUsersPage(page);
      setUsersNextPage(payload?.nextPage || null);

      if (facets && typeof facets === 'object') {
        const t = Array.isArray(facets.targetLanguages) ? facets.targetLanguages : [];
        const n = Array.isArray(facets.nativeLanguages) ? facets.nativeLanguages : [];
        setLanguageOptions(t);
        setNativeLanguageOptions(n);
        setUserFacets({
          targetLanguages: t,
          nativeLanguages: n,
          levels: Array.isArray(facets.levels) ? facets.levels : [],
        });
      } else {
        // Fallback: build options from returned results
        const targetSet = new Set();
        const nativeSet = new Set();
        results.forEach((u) => {
          (Array.isArray(u.target_languages) ? u.target_languages : []).forEach((l) => targetSet.add(l));
          (Array.isArray(u.native_languages) ? u.native_languages : []).forEach((l) => nativeSet.add(l));
        });
        setLanguageOptions(Array.from(targetSet));
        setNativeLanguageOptions(Array.from(nativeSet));
        setUserFacets({ targetLanguages: Array.from(targetSet), nativeLanguages: Array.from(nativeSet), levels: [] });
      }
      return results;
    } catch (e) {
      if (!append) setUsers([]);
      setUsersError(e.message || 'Failed to load users');
      return [];
    } finally {
      setUsersLoading(false);
    }
  };

  // Initial load + re-fetch when filters change (partners tab)
  useEffect(() => {
    if (activeTab !== 'users') return;
    if (!user?.id) return;
    setUsersNextPage(null);
    fetchUsersFromApi({ page: 1, sort: usersSort || 'best_match', append: false });
  }, [activeTab, user?.id, languageFilter, nativeLanguageFilter, levelFilter, debouncedUserSearch, myMatchPreferences, usersSort, includeConnectedUsers]);

  const fetchGroupsFromApi = async ({ page = 1, sort = groupsSort || 'best_match', overrideFilters = null, append = false } = {}) => {
    if (!user?.id) return;
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const filters = overrideFilters || {
        search: debouncedGroupSearch || '',
        language: groupLanguage || '',
        level: groupLevel || '',
        dayOfWeek: groupDayOfWeek || '',
        timeFrom: groupTimeFrom || '',
        timeTo: groupTimeTo || '',
      };
      const res = await authFetch('/api/discover/groups/search', {
        method: 'POST',
        body: {
          filters,
          sort,
          page,
          pageSize: 50,
        },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Failed to load groups');
      }
      const payload = await res.json();
      const results = payload?.results || [];
      const facets = payload?.facets || null;
      setGroups((prev) => (append ? [...(prev || []), ...(results || [])] : results));
      setGroupsPage(page);
      setGroupsNextPage(payload?.nextPage || null);
      if (facets && typeof facets === 'object') {
        setGroupFacets({
          languages: Array.isArray(facets.languages) ? facets.languages : [],
          levels: Array.isArray(facets.levels) ? facets.levels : [],
          days: Array.isArray(facets.days) ? facets.days : [],
        });
      }
      return results;
    } catch (e) {
      if (!append) setGroups([]);
      setGroupsError(e.message || 'Failed to load groups');
      setGroupFacets({ languages: [], levels: [], days: [] });
      return [];
    } finally {
      setGroupsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'groups') return;
    if (!user?.id) return;
    setGroupsNextPage(null);
    fetchGroupsFromApi({ page: 1, sort: groupsSort || 'best_match', append: false });
  }, [activeTab, user?.id, debouncedGroupSearch, groupLanguage, groupLevel, groupDayOfWeek, groupTimeFrom, groupTimeTo, groupsSort]);

  const fetchEventsFromApi = async ({ page = 1, sort = eventsSort || 'soonest', overrideFilters = null, append = false } = {}) => {
    if (!user?.id) return;
    setEventsLoading(true);
    setEventsError(null);
    try {
      const filters = overrideFilters || {
        search: debouncedEventSearch || '',
        language: eventLanguage || '',
        dateFrom: eventDateFrom || '',
        dateTo: eventDateTo || '',
        upcomingOnly: !!eventUpcomingOnly,
        attendingOnly: !!eventAttendingOnly,
      };
      const res = await authFetch('/api/discover/events/search', {
        method: 'POST',
        body: {
          filters,
          sort,
          page,
          pageSize: 50,
        },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Failed to load events');
      }
      const payload = await res.json();
      const results = payload?.results || [];
      const facets = payload?.facets || null;
      setAllEvents((prev) => (append ? [...(prev || []), ...(results || [])] : results));
      setEventsPage(page);
      setEventsNextPage(payload?.nextPage || null);
      if (facets && typeof facets === 'object') {
        setEventFacets({
          languages: Array.isArray(facets.languages) ? facets.languages : [],
          dateMin: facets.dateMin || null,
          dateMax: facets.dateMax || null,
        });
      }
      return results;
    } catch (e) {
      if (!append) setAllEvents([]);
      setEventsError(e.message || 'Failed to load events');
      setEventFacets({ languages: [], dateMin: null, dateMax: null });
      return [];
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'events') return;
    if (!user?.id) return;
    setEventsNextPage(null);
    fetchEventsFromApi({ page: 1, sort: eventsSort || 'soonest', append: false });
  }, [activeTab, user?.id, debouncedEventSearch, eventLanguage, eventDateFrom, eventDateTo, eventUpcomingOnly, eventAttendingOnly, eventsSort]);

  const handleLoadMoreUsers = async () => {
    if (!usersNextPage) return;
    await fetchUsersFromApi({ page: usersNextPage, sort: usersSort || 'best_match', append: true });
  };

  const handleLoadMoreGroups = async () => {
    if (!groupsNextPage) return;
    await fetchGroupsFromApi({ page: groupsNextPage, sort: groupsSort || 'best_match', append: true });
  };

  const handleLoadMoreEvents = async () => {
    if (!eventsNextPage) return;
    await fetchEventsFromApi({ page: eventsNextPage, sort: eventsSort || 'soonest', append: true });
  };

  const handleClearUserFilters = () => {
    setLanguageFilter('');
    setNativeLanguageFilter('');
    setLevelFilter('');
    setSearch('');
  };

  const handleClearGroupFilters = () => {
    setGroupSearch('');
    setGroupLanguage('');
    setGroupLevel('');
    setGroupDayOfWeek('');
  };

  const handleClearEventFilters = () => {
    setEventSearch('');
    setEventLanguage('');
    setEventDateFrom('');
    setEventDateTo('');
    setEventUpcomingOnly(true);
    setEventAttendingOnly(false);
  };

  useEffect(() => {
    const fetchConnectionRequests = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from('connections')
        .select('connection_id, status')
        .eq('user_id', user.id);

      if (!error && data) {
        const requests = {};
        data.forEach(req => {
          requests[req.connection_id] = req.status;
        });
        setConnectionRequests(requests);
      }
    };
    fetchConnectionRequests();
  }, [user]);

  // Fetch accepted connections to drive "Connected" badges in UserGrid
  useEffect(() => {
    const fetchAcceptedConnections = async () => {
      if (!user) return;
      try {
        const [{ data: outAcc }, { data: inAcc }] = await Promise.all([
          supabase.from('connections').select('connection_id').eq('user_id', user.id).eq('status', 'accepted'),
          supabase.from('connections').select('user_id').eq('connection_id', user.id).eq('status', 'accepted')
        ]);
        const ids = new Set([...(outAcc || []).map(r => r.connection_id), ...(inAcc || []).map(r => r.user_id)]);
        setConnectedUserIds(Array.from(ids));
      } catch (e) {
        setConnectedUserIds([]);
      }
    };
    fetchAcceptedConnections();
  }, [user]);

  useEffect(() => {
    const fetchIncomingRequests = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from('connections')
        .select('id, user_id, status')
        .eq('connection_id', user.id)
        .eq('status', 'pending');
      if (!error && data) {
        setIncomingRequests(data);
      }
    };
    fetchIncomingRequests();
  }, [user]);

  useEffect(() => {
    const fetchGroupMemberships = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('group_members')
          .select('id, group_id, user_id, joined_at, role')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching group memberships:', error);
          setGroupMemberships({});
          alert('Failed to fetch your group memberships. Please check your permissions or try again later.');
          return;
        }

        if (data) {
          const memberships = {};
          data.forEach(row => {
            memberships[row.group_id] = true;
          });
          setGroupMemberships(memberships);
        }
      } catch (err) {
        console.error('Unexpected error fetching group memberships:', err);
        setGroupMemberships({});
        alert('An unexpected error occurred while fetching group memberships.');
      }
    };
    fetchGroupMemberships();
  }, [user]);

  useEffect(() => {
    const fetchEventAttendance = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from('event_attendees')
        .select('event_id')
        .eq('user_id', user.id);
      if (!error && data) {
        const attendance = {};
        data.forEach(row => {
          attendance[row.event_id] = true;
        });
        setEventAttendance(attendance);
      }
    };
    fetchEventAttendance();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchNotifications = async () => {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (data) setNotifications(data);
    };
    fetchNotifications();
  }, [user]);

  // --- Notification Handlers ---
  const handleMarkAllNotificationsRead = async () => {
    if (!user) return;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id);
  };

  const handleToggleNotificationRead = async (notification) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notification.id ? { ...n, is_read: !notification.is_read } : n
      )
    );
    await supabase
      .from('notifications')
      .update({ is_read: !notification.is_read })
      .eq('id', notification.id);
  };

  const handleDeleteNotification = async (notification) => {
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    await supabase
      .from('notifications')
      .delete()
      .eq('id', notification.id);
  };

  const handleDeleteAllNotifications = async () => {
    if (!user) return;
    setNotifications([]);
    await supabase
      .from('notifications')
      .delete()
      .eq('user_id', user.id);
  };

  // --- Connection Handlers ---
  const handleAcceptRequest = async (request) => {
    await supabase
      .from('connections')
      .update({ status: 'accepted' })
      .eq('id', request.id);
    setIncomingRequests(prev => prev.filter(r => r.id !== request.id));
  };

  const handleSendConnectionRequest = async (userId) => {
    if (!user) return;
    try {
      // Create connection request
      await supabase
        .from('connections')
        .insert([{ user_id: user.id, connection_id: userId, status: 'pending' }]);

      // Create notification for the recipient (schema: uses 'data' and 'from_user_id')
      await supabase.from('notifications').insert([{
        user_id: userId,
        from_user_id: user.id,
        type: 'connection_request',
        title: 'New Connection Request',
        content: 'Someone wants to connect with you as a language partner!',
        is_read: false,
        data: { sender_id: user.id }
      }]);

      // Update local state
      setConnectionRequests(prev => ({ ...prev, [userId]: 'pending' }));

      alert('Connection request sent successfully!');
    } catch (error) {
      console.error('Error sending connection request:', error);
      alert('Failed to send connection request. Please try again later.');
    }
  };

  const handleRejectRequest = async (request) => {
    await supabase
      .from('connections')
      .update({ status: 'rejected' })
      .eq('id', request.id);
    setIncomingRequests(prev => prev.filter(r => r.id !== request.id));
  };

  const handleConnectWithSuggestion = async (userId) => {
    if (!user) return;
    try {
      // Send connection request
      await supabase
        .from('connections')
        .insert([{ user_id: user.id, connection_id: userId, status: 'pending' }]);

      // Create notification for the suggested user
      await supabase.from('notifications').insert([{
        user_id: userId,
        from_user_id: user.id,
        type: 'connection_request',
        title: 'New Connection Request',
        content: 'Someone wants to connect with you as a language partner!',
        is_read: false,
        data: { sender_id: user.id }
      }]);

      // Update local state
      setConnectionRequests(prev => ({ ...prev, [userId]: 'pending' }));
      
      alert('Connection request sent successfully!');
    } catch (error) {
      console.error('Error sending connection request:', error);
      alert('Failed to send connection request. Please try again later.');
    }
  };

  // --- Group Handlers ---
  const handleJoinGroup = async (groupId) => {
    if (!user) return;
    try {
      await supabase
        .from('group_members')
        .insert([{ group_id: groupId, user_id: user.id, role: 'member' }]);
      setGroupMemberships(prev => ({ ...prev, [groupId]: true }));
    } catch (err) {
      alert('Failed to join group: ' + err.message);
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!user) return;
    try {
      await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', user.id);
      setGroupMemberships(prev => {
        const updated = { ...prev };
        delete updated[groupId];
        return updated;
      });
    } catch (err) {
      alert('Failed to leave group: ' + err.message);
    }
  };

  // --- Event Handlers ---
  const handleAttendEvent = async (eventId) => {
    if (!user) return;
    try {
      await supabase
        .from('event_attendees')
        .insert([{ event_id: eventId, user_id: user.id }]);
      setEventAttendance(prev => ({ ...prev, [eventId]: true }));
    } catch (err) {
      alert('Failed to attend event: ' + err.message);
    }
  };

  const handleCreateEvent = async () => {
    if (!user) return;
    try {
      // Validate required fields
      if (!newEvent.title || !newEvent.date || !newEvent.time) {
        alert('Please fill in all required fields (title, date, and time)');
        return;
      }

      // Format the date and time properly
      const formattedDate = new Date(newEvent.date).toISOString().split('T')[0];
      const formattedTime = newEvent.time.includes(':') ? newEvent.time : `${newEvent.time}:00`;

      // Create event data object
      const eventData = {
        title: newEvent.title.trim(),
        description: (newEvent.description || '').trim(),
        date: formattedDate,
        time: formattedTime,
        language: (newEvent.language || '').trim(),
        created_by: user.id,
        created_at: new Date().toISOString()
      };

      console.log('Creating event with data:', eventData);

      // Create the event with error checking
      const eventResponse = await supabase
        .from('events')
        .insert([eventData])
        .select()
        .single();

      if (eventResponse.error) {
        console.error('Event creation error:', eventResponse.error);
        throw eventResponse.error;
      }

      const createdEvent = eventResponse.data;

      // Add creator as attendee with error checking
      const attendeeResponse = await supabase
        .from('event_attendees')
        .insert([{
          event_id: createdEvent.id,
          user_id: user.id,
          role: 'host',
          joined_at: new Date().toISOString()
        }]);

      if (attendeeResponse.error) {
        console.error('Attendee creation error:', attendeeResponse.error);
        throw attendeeResponse.error;
      }

      // Update local state
      setAllEvents(prev => [...prev, createdEvent]);
      setEventAttendance(prev => ({ ...prev, [createdEvent.id]: true }));
      setIsCreatingEvent(false);
      setNewEvent({
        title: '',
        description: '',
        date: '',
        time: '',
        language: '',
        created_by: user.id,
        created_at: new Date().toISOString()
      });

      alert('Event created successfully!');
      return createdEvent;
    } catch (error) {
      console.error('Detailed error:', error);
      alert(`Failed to create event: ${error.message || 'Unknown error'}`);
    }
  };

  const handleSelectEvent = async (event) => {
    setSelectedEvent(event);
    
    // Fetch attendees for the selected event
    const { data: attendees, error } = await supabase
      .from('event_attendees')
      .select(`
        user_id,
        role,
        profiles:user_id (
          id,
          name,
          avatar_url
        )
      `)
      .eq('event_id', event.id);

    if (!error && attendees) {
      setEventAttendees(prev => ({
        ...prev,
        [event.id]: attendees.map(a => ({
          id: a.profiles.id,
          name: a.profiles.name,
          avatar_url: a.profiles.avatar_url,
          role: a.role
        }))
      }));
    }
  };

  const handleLeaveEvent = async (eventId) => {
    if (!user) return;
    try {
      await supabase
        .from('event_attendees')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', user.id);
      setEventAttendance(prev => {
        const updated = { ...prev };
        delete updated[eventId];
        return updated;
      });
    } catch (err) {
      alert('Failed to leave event: ' + err.message);
    }
  };

  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent(prev => ({ ...prev, [name]: value }));
  };

  const handleInviteToGroupEvent = async (userId, type, itemId) => {
    if (!user) return;
    try {
      // Create an invitation notification
      await supabase.from('notifications').insert([{
        user_id: userId,
        from_user_id: user.id,
        type: 'invitation',
        title: `New ${type} Invitation`,
        content: `You've been invited to join a ${type.toLowerCase()}`,
        is_read: false,
        data: { type, id: itemId, inviter_id: user.id }
      }]);

      // Show confirmation to the inviter
      alert(`Invitation sent successfully!`);
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Failed to send invitation');
    }
  };

  const handleFindMatches = async () => {
    if (!user) return;

    const demographicsFromPrefs = buildDemographicsFromPreferences(myMatchPreferences);

    // v1: Use server-side best_match ranking with current filters (or none).
    const results = await fetchUsersFromApi({
      page: 1,
      sort: 'best_match',
      overrideFilters: {
        targetLanguages: languageFilter ? [languageFilter] : [],
        nativeLanguages: nativeLanguageFilter ? [nativeLanguageFilter] : [],
        levels: levelFilter ? [levelFilter] : [],
        interests: [],
        search: '',
        demographics: demographicsFromPrefs || undefined,
      },
    });
    setMatchSuggestions(results || []);
    setShowSuggestions(true);
  };

  const handleReportUser = async (userId, reason) => {
    if (!user) return;
    try {
      // Create a report entry
      await supabase.from('reports').insert([{
        reporter_id: user.id,
        reported_user_id: userId,
        reason: reason,
        status: 'pending',
        created_at: new Date().toISOString()
      }]);

      // Create a notification for admins
      await supabase.from('notifications').insert([{
        user_id: userId, // recipient (e.g., admin filter)
        from_user_id: user.id,
        type: 'report',
        title: 'New User Report',
        content: `A user has been reported for: ${reason}`,
        is_read: false,
        data: { reporter_id: user.id, reported_user_id: userId }
      }]);

      alert('Report submitted successfully. Our moderators will review it.');
    } catch (error) {
      console.error('Error reporting user:', error);
      alert('Failed to submit report. Please try again later.');
    }
  };

  const handleBlockUser = async (userId) => {
    if (!user) return;
    try {
      // Add to blocked users table
      await supabase.from('blocked_users').insert([{
        user_id: user.id,
        blocked_user_id: userId,
        created_at: new Date().toISOString()
      }]);

      // Remove any existing connections
      await supabase
        .from('connections')
        .delete()
        .or(`user_id.eq.${user.id},connection_id.eq.${user.id}`)
        .or(`user_id.eq.${userId},connection_id.eq.${userId}`);

      // Update local state to reflect the block
      setUsers(prev => prev.filter(u => u.id !== userId));
      
      alert('User has been blocked successfully.');
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user. Please try again later.');
    }
  };

    const handleRemoveConnection = async (userId) => {
      if (!user) return;
      try {
        // Remove the connection from both directions
        await supabase
          .from('connections')
          .delete()
          .or(`and(user_id.eq.${user.id},connection_id.eq.${userId}),and(user_id.eq.${userId},connection_id.eq.${user.id})`);
    
        // Update local state
        setConnectionRequests(prev => {
          const updated = { ...prev };
          delete updated[userId];
          return updated;
        });
    
        alert('Connection removed successfully.');
      } catch (error) {
        console.error('Error removing connection:', error);
        alert('Failed to remove connection. Please try again later.');
      }
    };

      const [groupMembers, setGroupMembers] = useState({}); // NEW
      
      useEffect(() => {
        const fetchAllGroupMembers = async () => {
          const { data, error } = await supabase
            .from('group_members')
            .select('group_id, user_id, role, joined_at');
          if (!error && data) {
            // Group by group_id
            const membersByGroup = {};
            data.forEach(row => {
              if (!membersByGroup[row.group_id]) membersByGroup[row.group_id] = [];
              membersByGroup[row.group_id].push(row);
            });
            setGroupMembers(membersByGroup);
          }
        };
        fetchAllGroupMembers();
      }, []);

      // ... after handleBlockUser ...

    const handleSendGift = async (userId, giftType) => {
      if (!user) return;
      try {
        // Create a gift record in user_gifts table
        await supabase.from('user_gifts').insert([{
          sender_id: user.id,
          recipient_id: userId,
          gift_type: giftType,
          created_at: new Date().toISOString()
        }]);
    
        // Create a notification for the recipient
        await supabase.from('notifications').insert([{
          user_id: userId,
          type: 'gift',
          title: 'New Gift Received',
          content: `You received a ${giftType} from a language partner!`,
          is_read: false,
          metadata: {
            sender_id: user.id,
            gift_type: giftType
          }
        }]);
    
        alert('Gift sent successfully!');
      } catch (error) {
        console.error('Error sending gift:', error);
        alert('Failed to send gift. Please try again later.');
      }
    };
      // ... after handleSendGift and before filteredUsers definition ...
  const handleEditEvent = async (eventId, updatedData) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('events')
        .update(updatedData)
        .eq('id', eventId);

      if (error) throw error;

      setAllEvents(prev => 
        prev.map(event => 
          event.id === eventId ? { ...event, ...updatedData } : event
        )
      );
      setSelectedEvent(null);
      alert('Event updated successfully!');
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event');
    }
  };

  const handleCancelEvent = async (eventId) => {
    if (!user) return;
    try {
      const { data: attendees } = await supabase
        .from('event_attendees')
        .select('user_id')
        .eq('event_id', eventId);

      if (attendees) {
        const notifications = attendees.map(({ user_id: attendee_id }) => ({
          user_id: attendee_id,
          from_user_id: user.id,
          type: 'event_cancelled',
          title: 'Event Cancelled',
          content: `An event you were planning to attend has been cancelled`,
          is_read: false,
          data: { event_id: eventId }
        }));

        await supabase.from('notifications').insert(notifications);
      }

      await Promise.all([
        supabase.from('events').delete().eq('id', eventId),
        supabase.from('event_attendees').delete().eq('event_id', eventId)
      ]);

      setAllEvents(prev => prev.filter(event => event.id !== eventId));
      setSelectedEvent(null);
      alert('Event cancelled successfully');
    } catch (error) {
      console.error('Error cancelling event:', error);
      alert('Failed to cancel event');
    }
  };

  const handleShareEvent = async (eventId) => {
    try {
      const eventLink = `${window.location.origin}/events/${eventId}`;
      await navigator.clipboard.writeText(eventLink);
      alert('Event link copied to clipboard!');
    } catch (error) {
      console.error('Error sharing event:', error);
      alert('Failed to share event');
    }
  };

  // --- Filtering logic ---
  const filteredUsers = users.filter(u => {
    const matchesLanguage = !languageFilter || (u.target_languages || []).includes(languageFilter);
    const matchesNative = !nativeLanguageFilter || (u.native_languages || []).includes(nativeLanguageFilter);
    const matchesLevel = !levelFilter || u.level === levelFilter;
    const matchesSearch = !search || (u.display_name || u.name || '').toLowerCase().includes(search.toLowerCase());
    return matchesLanguage && matchesNative && matchesLevel && matchesSearch;
  });

  const filteredGroups = groups.filter(g => true);
  const filteredEvents = allEvents.filter(e => true);

  return (
    <div className="discover-section">
      <div className="page-panel">
        <DiscoverTabs activeTab={activeTab} setActiveTab={setActiveTab} />

        {activeTab === 'users' && (
          <div className="filter-container" style={{ marginBottom: 10 }}>
            <div className="filter-group">
              <label className="filter-label">Saved:</label>
              <select
                className="filter-select"
                value={selectedSavedSearchId}
                onChange={(e) => setSelectedSavedSearchId(e.target.value)}
              >
                <option value="">Select saved search…</option>
                {(savedSearches || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="button-group">
              <button className="find-matches-btn" onClick={handleApplySavedSearch} disabled={!selectedSavedSearchId}>
                Apply
              </button>
              <button className="find-matches-btn" onClick={handleSaveCurrentSearch}>
                Save
              </button>
              <button className="find-matches-btn" onClick={handleDeleteSavedSearch} disabled={!selectedSavedSearchId}>
                Delete
              </button>
            </div>
          </div>
        )}
        
        <DiscoverFilters
          languageFilter={languageFilter}
          setLanguageFilter={setLanguageFilter}
          languageOptions={languageOptions}
          nativeLanguageFilter={nativeLanguageFilter}
          setNativeLanguageFilter={setNativeLanguageFilter}
          nativeLanguageOptions={nativeLanguageOptions}
          levelFilter={levelFilter}
          setLevelFilter={setLevelFilter}
          levelOptions={userFacets.levels}
          usersSort={usersSort}
          setUsersSort={setUsersSort}
          search={search}
          setSearch={setSearch}
          handleFindMatches={handleFindMatches}
          activeTab={activeTab}
          includeConnectedUsers={includeConnectedUsers}
          setIncludeConnectedUsers={setIncludeConnectedUsers}

          groupSearch={groupSearch}
          setGroupSearch={setGroupSearch}
          groupLanguage={groupLanguage}
          setGroupLanguage={setGroupLanguage}
          groupLevel={groupLevel}
          setGroupLevel={setGroupLevel}
          groupDayOfWeek={groupDayOfWeek}
          setGroupDayOfWeek={setGroupDayOfWeek}
          groupTimeFrom={groupTimeFrom}
          setGroupTimeFrom={setGroupTimeFrom}
          groupTimeTo={groupTimeTo}
          setGroupTimeTo={setGroupTimeTo}
          groupLanguageOptions={groupFacets.languages || []}
          groupDayOptions={groupFacets.days || []}
          groupsSort={groupsSort}
          setGroupsSort={setGroupsSort}

          eventSearch={eventSearch}
          setEventSearch={setEventSearch}
          eventLanguage={eventLanguage}
          setEventLanguage={setEventLanguage}
          eventDateFrom={eventDateFrom}
          setEventDateFrom={setEventDateFrom}
          eventDateTo={eventDateTo}
          setEventDateTo={setEventDateTo}
          eventLanguageOptions={eventFacets.languages || []}
          eventUpcomingOnly={eventUpcomingOnly}
          setEventUpcomingOnly={setEventUpcomingOnly}
          eventAttendingOnly={eventAttendingOnly}
          setEventAttendingOnly={setEventAttendingOnly}
          eventsSort={eventsSort}
          setEventsSort={setEventsSort}
        />

        {activeTab === 'users' && (
          <>
            <IncomingRequests
              incomingRequests={incomingRequests}
              users={users}
              handleAcceptRequest={handleAcceptRequest}
              handleRejectRequest={handleRejectRequest}
            />
            <UserGrid
              users={filteredUsers}
              connectionRequests={connectionRequests}
              handleSendConnectionRequest={handleSendConnectionRequest}
              currentUser={user}
              connectedUserIds={connectedUserIds}
              onInviteToGroupEvent={handleInviteToGroupEvent}
              onReportUser={handleReportUser}
              onBlockUser={handleBlockUser}
              onSendGift={handleSendGift}
              handleRemoveConnection={handleRemoveConnection}
            />
            {usersLoading && (filteredUsers || []).length === 0 ? (
              <div className="skeleton-grid">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="skeleton-card">
                    <div className="skeleton-row">
                      <div className="skeleton-avatar" />
                      <div style={{ flex: 1 }}>
                        <div className="skeleton-line lg" style={{ width: '70%' }} />
                        <div className="skeleton-spacer" />
                        <div className="skeleton-line sm" style={{ width: '45%' }} />
                      </div>
                    </div>
                    <div className="skeleton-spacer" />
                    <div className="skeleton-line" style={{ width: '95%' }} />
                    <div className="skeleton-spacer" />
                    <div className="skeleton-line" style={{ width: '80%' }} />
                  </div>
                ))}
              </div>
            ) : null}
            {usersError ? <div style={{ marginTop: 12, color: 'salmon' }}>{usersError}</div> : null}
            {!usersLoading && !usersError && filteredUsers.length === 0 ? (
              <div style={{ marginTop: 12 }}>
                <div>No matching partners found.</div>
                <div style={{ marginTop: 8 }}>
                  <button className="find-matches-btn" onClick={handleClearUserFilters}>
                    Clear filters
                  </button>
                </div>
              </div>
            ) : null}
            <div className="discover-list-footer">
              {usersLoading && (filteredUsers || []).length > 0 ? (
                <div className="discover-inline-loading">Loading more…</div>
              ) : usersNextPage ? (
                <button className="find-matches-btn" onClick={handleLoadMoreUsers} disabled={usersLoading}>
                  Load more
                </button>
              ) : null}
            </div>
            <SuggestionsModal
              showSuggestions={showSuggestions}
              setShowSuggestions={setShowSuggestions}
              matchSuggestions={matchSuggestions}
              connectionRequests={connectionRequests}
              handleConnectWithSuggestion={handleConnectWithSuggestion}
            />
          </>
        )}

        {activeTab === 'groups' && (
          <>
            {groupsLoading && (filteredGroups || []).length === 0 ? (
              <div className="skeleton-grid">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="skeleton-card">
                    <div className="skeleton-line lg" style={{ width: '75%' }} />
                    <div className="skeleton-spacer" />
                    <div className="skeleton-line sm" style={{ width: '55%' }} />
                    <div className="skeleton-spacer" />
                    <div className="skeleton-line" style={{ width: '92%' }} />
                    <div className="skeleton-spacer" />
                    <div className="skeleton-line" style={{ width: '70%' }} />
                  </div>
                ))}
              </div>
            ) : null}
            {groupsError ? <div style={{ marginTop: 12, color: 'salmon' }}>{groupsError}</div> : null}
            {!groupsLoading && !groupsError && filteredGroups.length === 0 ? (
              <div style={{ marginTop: 12 }}>
                <div>No matching groups found.</div>
                <div style={{ marginTop: 8 }}>
                  <button className="find-matches-btn" onClick={handleClearGroupFilters}>
                    Clear filters
                  </button>
                </div>
              </div>
            ) : null}
            <div className="discover-list-footer">
              {groupsLoading && (filteredGroups || []).length > 0 ? (
                <div className="discover-inline-loading">Loading more…</div>
              ) : groupsNextPage ? (
                <button className="find-matches-btn" onClick={handleLoadMoreGroups} disabled={groupsLoading}>
                  Load more
                </button>
              ) : null}
            </div>
            <GroupGrid
              groups={filteredGroups}
              groupMemberships={groupMemberships}
              onJoinGroup={handleJoinGroup}
              onLeaveGroup={handleLeaveGroup}
              currentUser={user}
            />
          </>
        )}

        {activeTab === 'events' && (
          <>
            {eventsLoading && (filteredEvents || []).length === 0 ? (
              <div className="skeleton-grid">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="skeleton-card">
                    <div className="skeleton-line lg" style={{ width: '78%' }} />
                    <div className="skeleton-spacer" />
                    <div className="skeleton-line sm" style={{ width: '40%' }} />
                    <div className="skeleton-spacer" />
                    <div className="skeleton-line sm" style={{ width: '55%' }} />
                    <div className="skeleton-spacer" />
                    <div className="skeleton-line" style={{ width: '90%' }} />
                  </div>
                ))}
              </div>
            ) : null}
            {eventsError ? <div style={{ marginTop: 12, color: 'salmon' }}>{eventsError}</div> : null}
            {!eventsLoading && !eventsError && filteredEvents.length === 0 ? (
              <div style={{ marginTop: 12 }}>
                <div>No matching events found.</div>
                <div style={{ marginTop: 8 }}>
                  <button className="find-matches-btn" onClick={handleClearEventFilters}>
                    Reset filters
                  </button>
                </div>
              </div>
            ) : null}
            <div className="discover-list-footer">
              {eventsLoading && (filteredEvents || []).length > 0 ? (
                <div className="discover-inline-loading">Loading more…</div>
              ) : eventsNextPage ? (
                <button className="find-matches-btn" onClick={handleLoadMoreEvents} disabled={eventsLoading}>
                  Load more
                </button>
              ) : null}
            </div>
            <EventSection
              isCreatingEvent={isCreatingEvent}
              setIsCreatingEvent={setIsCreatingEvent}
              selectedEvent={selectedEvent}
              setSelectedEvent={setSelectedEvent}
              allEvents={filteredEvents}
              eventAttendance={eventAttendance}
              handleAttendEvent={handleAttendEvent}
              handleLeaveEvent={handleLeaveEvent}
              handleCreateEvent={handleCreateEvent}
              handleEditEvent={handleEditEvent}
              handleCancelEvent={handleCancelEvent}
              handleShareEvent={handleShareEvent}
              newEvent={newEvent}
              setNewEvent={setNewEvent}
              handleEventInputChange={handleEventInputChange}
              currentUser={user}
              eventAttendees={eventAttendees}
            />
          </>
        )}

        <NotificationsPanel
          notifications={notifications}
          showNotifications={showNotifications}
          setShowNotifications={setShowNotifications}
          handleMarkAllNotificationsRead={handleMarkAllNotificationsRead}
          handleToggleNotificationRead={handleToggleNotificationRead}
          handleDeleteNotification={handleDeleteNotification}
          handleDeleteAllNotifications={handleDeleteAllNotifications}
        />
      </div>
    </div>
  );
};

export default Discover;